import React, { useState, useEffect, useContext } from 'react'

import { useDispatch, useSelector } from 'react-redux'

import { CPF } from 'gerador-validador-cpf'

import queryString from 'query-string'

import {
  clearMedicalRequest,
  getPlanGroups,
  sendProcedureRequest,
  registerInfoGroup,
} from 'redux/actions'

import { SignupContext } from 'contexts/SignupContext'

import Dropdown from 'components/Dropdown'
import Register from '../Register'
import Row from 'components/Row'
import { Typography, Button, CoreIcons, TextInput, Spinner, Column } from 'components'

import { stepsList } from './Steps'
import { PATIENT } from 'settings/_profileSettings'
import { planGroupsSettings, PlanGroupCategoryEnum } from 'settings/_planGroupSettings'
import { history } from 'routers/AppRouter'
import * as routes from 'settings/_routesSettings'
import axios from '../../../settings/axios'
import { apiEndpoints } from 'settings/_apiSettings'
import { CAREPLUS_PLAN_GROUP_CODE } from 'settings/_planGroupCode'

const InfoGroup = () => {
  const planGroupCodeFromURL = queryString.parse(window.location.search).group_plan

  const {
    setPlanGroup,
    setCpf,
    cpf,
    cpfMsg,
    isCpfValid,
    listPlanGroups,
  } = useContext(SignupContext)
  
  const dispatch = useDispatch()

  const [isLoadingProcedure, setIsLoadingProcedure] = useState(false)
  const [cardNumberExists, setCardNumberExists] = useState(false)
  const [isReadyToSubmit, setIsReadyToSubmit] = useState(false)
  const [fields, setFields] = useState({
    plan_code: { value: '' },
    card_number: {
      value: '',
      isValid: false
    },
    hasMedicRequest: false,
    medicRequest: {
      isValid: true,
      message: '',
      placeholder: 'Anexar pedido médico',
      value: '',
      displayValue: ''
    }
  })
  
  const [planGroupList, setPlanGroupList] = useState([])
  const [planGroupType, setPlanGroupType] = useState(PlanGroupCategoryEnum.PARTICULAR)
  const procedureError = useSelector(state => state.medicalRequests.errorMessage)
  const foundPlanGroup = planGroupList?.find(planGroup => planGroup.code === planGroupCodeFromURL)

  const plansPlaceholder = settings && settings.plansPlaceholder ? settings.plansPlaceholder : 'Tipo de plano'
  const settings = planGroupsSettings[foundPlanGroup?.code]
  
  const showCPFInput = () => {
    const planGroupWithCPFIdentifier = [
      PlanGroupCategoryEnum.CORPORATE_LIMITED,
      PlanGroupCategoryEnum.CORPORATE_UNLIMITED,
      PlanGroupCategoryEnum.CAPITATION].includes(planGroupType)

    if (planGroupWithCPFIdentifier || settings?.useCpfInCardNumber) {
      return true
    }

    return false
  }
  
  useEffect(() => {
    async function getPlanGroupsList() {
      const response = await listPlanGroups()
      
      setPlanGroupList(response)
    }

    getPlanGroupsList()
  }, [])

  useEffect(() => {
    function checkFields() {
      let isReadyToSubmit = false

      if (showCPFInput()) {
        isReadyToSubmit = isCpfValid
      } else {
        isReadyToSubmit = fields.card_number.isValid
        if (planGroupType === PlanGroupCategoryEnum.CARE_PLUS) {
          isReadyToSubmit = fields.card_number.isValid
        }
      }

      setIsReadyToSubmit(fields.plan_code.value !== '' && isReadyToSubmit)
    }

    checkFields()
    dispatch(clearMedicalRequest())

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fields, cpf, isCpfValid])

  useEffect(() => {
    function selectFlow() {
      const isGroupPlanDefined = planGroupCodeFromURL !== '' && planGroupCodeFromURL !== null

      if (isGroupPlanDefined && !planGroupType !== PlanGroupCategoryEnum.PARTICULAR) {
        dispatch(getPlanGroups(planGroupCodeFromURL, { plan_profile_role: PATIENT }))
      }
    }

    selectFlow()

  }, [planGroupCodeFromURL])

  useEffect(() => {
    if (!foundPlanGroup) return

    setPlanGroup({
      code: foundPlanGroup.code,
      name: foundPlanGroup.name,
      category: foundPlanGroup.category,
      use_atena: foundPlanGroup.use_atena
    })
    
    const newPlanGroupType = getCurrentPlanGroupType(foundPlanGroup)
    
    setPlanGroupType(newPlanGroupType)
  }, [foundPlanGroup])

  function getCurrentPlanGroupType(planGroup) {
    if (planGroup.code === CAREPLUS_PLAN_GROUP_CODE) return PlanGroupCategoryEnum.CARE_PLUS
    if (planGroup.category === PlanGroupCategoryEnum.CORPORATE_LIMITED ) return PlanGroupCategoryEnum.CORPORATE_LIMITED
    if (planGroup.category === PlanGroupCategoryEnum.CORPORATE_UNLIMITED) return PlanGroupCategoryEnum.CORPORATE_UNLIMITED
    if (planGroup.category === PlanGroupCategoryEnum.CAPITATION) return PlanGroupCategoryEnum.CAPITATION
    
    return PlanGroupCategoryEnum.ENTERPRISE
  }

  function formatPlansDropdown() {
    if (foundPlanGroup && foundPlanGroup.plans) {
      return foundPlanGroup.plans.map(plan => {
        return {
          code: plan.id,
          name: plan.name
        }
      })
    }

    return []
  }

  function handlePlanSelection(code) {
    setFields(prevState => ({
      ...prevState,
      plan_code: {
        ...prevState.plan_code,
        value: code,
      }
    }))
  }

  function handleCardNumber(e, mask) {
    const inputValue = e.target.value
    const inputCleanedValue = inputValue.replace(/_+/g, '').replace(/\.+/g, '')
    const minLength = mask && mask.match(/1/g)?.length

    setFields(prevState => ({
      ...prevState,
      card_number: {
        ...prevState.card_number,
        isValid: mask ? inputCleanedValue.length === minLength : inputCleanedValue.length >= 1,
        value: inputCleanedValue,
      }
    }))
  }

  async function handleProcedureRequest() {
    setIsLoadingProcedure(true)

    const { errorMessage, startDate, planPassword } = await dispatch(sendProcedureRequest(
      fields.card_number.value,
      CPF.format(cpf, 'digits'),
      foundPlanGroup.plans.find(plan => plan.id === fields.plan_code.value).code
    ))

    setIsLoadingProcedure(false)
    return {
      errorMessage,
      startDate,
      planPassword,
    }
  }

  async function handleCheckCardNumberExists() {
    try {
      setIsLoadingProcedure(true)

      const cardNumber = settings.useCpfInCardNumber ? CPF.format(cpf, 'digits') : fields.card_number.value

      const url = apiEndpoints.PERSON_PROFILE_BY_CARD(cardNumber, foundPlanGroup.code)
      await axios.head(url)

      setIsLoadingProcedure(false)

      return {
        cardNumberExists: true
      }
    } catch (err) {
      setIsLoadingProcedure(false)
      if (err.response.status === 404) {
        setIsLoadingProcedure(false)
        return {
          cardNumberExists: false
        }
      }
    }
  }

  async function handleSubmit() {

    dispatch(clearMedicalRequest())

    let formattedData = {
      partner_plan_id: fields.plan_code.value,
      plan_validity: {}
    }

    if (planGroupType === PlanGroupCategoryEnum.CARE_PLUS || planGroupType === PlanGroupCategoryEnum.ENTERPRISE) {

      formattedData = {
        ...formattedData,
        card_number: settings.useCpfInCardNumber ? CPF.format(cpf, 'digits') : fields.card_number.value
      }

      const { cardNumberExists } = await handleCheckCardNumberExists()

      if (cardNumberExists) {
        setCardNumberExists(true)
        return
      }
    }

    if ([
      PlanGroupCategoryEnum.CORPORATE_LIMITED, 
      PlanGroupCategoryEnum.CORPORATE_UNLIMITED, 
      PlanGroupCategoryEnum.CAPITATION
    ].includes(planGroupType)) {

      const { errorMessage } = await handleProcedureRequest()

      if (errorMessage)
        return

      formattedData = {
        ...formattedData,
        cpf: CPF.format(cpf, 'digits'),
      }
    }

    let stepData = {
      plans: [formattedData],
      hasPlanCode: fields.plan_code.value !== ''
    }

    await dispatch(registerInfoGroup(stepData))
    history.push(routes.REGISTER_INFO_PERSONAL)
  }

  function onPrevStep() {
    setCpf(null)
    history.goBack()
  }

  const loading = !foundPlanGroup || !planGroupType

  return (
    <Register
      showStepper={true}
      showLoginInfo={false}
      currentStep={stepsList.INFO_GROUP}
    >
      {loading
        ? <Spinner height='30vh' />
        : (
          <div className='Register__InfoGroup'>
            <div className='Register__InfoGroup__Head'>
              <div className='Register__InfoGroup__Head__Image'>
                <img
                  src={foundPlanGroup.logo_url}
                  alt={`${foundPlanGroup.code}-${foundPlanGroup.name}`}
                  title={foundPlanGroup.name}
                />
              </div>

              <div className='Register__InfoGroup__Head__Text'>
                <Typography align='left'>Insira os dados do seu</Typography>
                <Typography align='left'>
                  {' '}
                  convênio <b>{foundPlanGroup.name}:</b>{' '}
                </Typography>
              </div>
            </div>

            <div className='Register__InfoGroup__Forms'>
              <div className='Register__InfoGroup__Forms__Item'>
                <Dropdown
                  name='plan'
                  options={formatPlansDropdown()}
                  onSelect={code => handlePlanSelection(code)}
                  placeholder={plansPlaceholder}
                  dropDownOverflow
                  dropDownItensToShow='3'
                  dataCy='signup-plan-type'
                  optionsListCustomClassName='Register__InfoGroup__Forms__Dropdown'
                />
              </div>
              {showCPFInput() ? (
                <div className='Register__InfoGroup__Forms__Item'>
                  <TextInput
                    placeholder='CPF (11 dígitos)'
                    name='cpf'
                    hasError={cpfMsg}
                    inputHeight='4.8rem'
                    mask='111.111.111-**'
                    messageError={cpfMsg}
                    onInputChange={e => setCpf(e.target.value)}
                    value={cpf}
                  />
                </div>
              ) : (
                <div className='Register__InfoGroup__Forms__Item'>
                  <TextInput
                    name='card_number'
                    placeholder={settings?.identifier.placeholder}
                    mask={settings?.identifier.mask ? settings?.identifier.mask : false}
                    value={fields.card_number.value}
                    onInputChange={e => handleCardNumber(e, settings?.identifier.mask)}
                    data-cy='signup-card-number'
                  />
                </div>
              )}

              {isLoadingProcedure && <Spinner height='5rem' />}
              {(procedureError || cardNumberExists) && (
                <div className='Register__InfoGroup__Error'>
                  <div className='Register__InfoGroup__Error__Icon'>
                    <CoreIcons
                      fill='#FF4949'
                      name='Info'
                      width='22px'
                      height='22px'
                      viewBox='00 00 22 22'
                    />
                  </div>
                  <div className='Register__InfoGroup__Error__Text'>
                    <Typography color='false'>
                      {procedureError}
                      {cardNumberExists && 'Carteirinha já cadastrada'}
                    </Typography>
                  </div>
                </div>
              )}
            </div>

            {!isLoadingProcedure && (
              <Row align='space-between' direction='row' margin='10px 0 0' wrap='nowrap'>
                <Column colWidth={6} rightGutter>
                  <Button
                    text='Voltar'
                    classModifier='reverse'
                    onButtonClick={onPrevStep}
                  />
                </Column>

                <Column colWidth={6}>
                  <Button
                    disabled={!isReadyToSubmit}
                    id='submit'
                    type='submit'
                    text='Próximo'
                    onButtonClick={handleSubmit}
                    data-cy='signup-next-step'
                  />
                </Column>
              </Row>
            )}
          </div>
        )}
    </Register>
  )
}

export default InfoGroup
